import axios, { AxiosInstance, AxiosResponse } from "axios";
import type { APIResponse, IScanQRCodeData } from "@/types";

// Create axios instance
const createApiInstance = (): AxiosInstance => {
  const baseURL = import.meta.env.VITE_API_ENDPOINT || "http://localhost/";

  const instance = axios.create({
    baseURL,
    timeout: 60000, // Match Vue project timeout
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  // Request interceptor
  instance.interceptors.request.use(
    (config) => {
      return config;
    },
    (error) => {
      console.error("❌ Request error:", error);
      return Promise.reject(error);
    },
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    (error) => {
      // Chỉ log chi tiết ở DEV. Ở production KHÔNG in body (có thể chứa dữ liệu
      // nhạy cảm như SĐT khách), chỉ status/url — sourcemap prod đang bật.
      if (import.meta.env.DEV) {
        console.error("❌ Response error:", {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url,
          data: error.response?.data,
        });
      } else {
        console.error("❌ Response error:", error.response?.status, error.config?.url);
      }

      return Promise.reject(error);
    },
  );

  return instance;
};

const apiInstance = createApiInstance();

// Utility function to trim string properties (from Vue project)
const trimStringProperties = (obj: any): any => {
  if (!obj || typeof obj !== "object") return obj;

  const trimmed: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === "string") {
      trimmed[key] = value.trim();
    } else {
      trimmed[key] = value;
    }
  }
  return trimmed;
};

// 🆕 API Init Setting - Load app configuration
export const apiInitSetting = async (): Promise<APIResponse<any>> => {
  try {
    const response = await apiInstance.post("/api/v1/verify/init-setting");
    return response.data;
  } catch (error) {
    console.error("❌ Error in apiInitSetting:", error);
    throw error;
  }
};

// API Scan QR Code - Updated to match Vue endpoint
export const apiScanQRCode = async (data: Record<string, string>): Promise<APIResponse<IScanQRCodeData>> => {
  try {
    const cleanData = trimStringProperties(data);
    const response = await apiInstance.post("/api/v1/verify/scan-qrcode", cleanData);
    return response.data;
  } catch (error) {
    console.error("❌ Error in apiScanQRCode:", error);
    throw error;
  }
};

// API chọn nhà mạng nhận thẻ cào (C14 — luồng verify/Template 1).
// Trả lại nguyên response scan-qrcode (đã kèm scratch_card_reward mới).
export const apiScanSelectTelecom = async (payload: {
  xid?: string;
  serial?: string;
  network: number;
}): Promise<APIResponse<IScanQRCodeData>> => {
  try {
    const cleanData = trimStringProperties(payload);
    const response = await apiInstance.post("/api/v1/verify/scan-select-telecom", cleanData);
    return response.data;
  } catch (error) {
    console.error("❌ Error in apiScanSelectTelecom:", (error as Error)?.message);
    throw error;
  }
};

// API nhập SĐT để nhận nạp tự động (D6 — luồng verify/Template 1).
// Trả lại nguyên response scan-qrcode (đã kèm scratch_card_reward mới).
export const apiScanSetTopupPhone = async (payload: {
  xid?: string;
  serial?: string;
  phone: string;
}): Promise<APIResponse<IScanQRCodeData>> => {
  try {
    const cleanData = trimStringProperties(payload);
    const response = await apiInstance.post("/api/v1/verify/scan-set-topup-phone", cleanData);
    return response.data;
  } catch (error) {
    // Không log nguyên error (chứa request body có SĐT). Chỉ message.
    console.error("❌ Error in apiScanSetTopupPhone:", (error as Error)?.message);
    throw error;
  }
};

// API Verify Stamp Code - For form submissions
export const apiVerifyStampCode = async (data: any): Promise<APIResponse<IScanQRCodeData>> => {
  try {
    const cleanData = trimStringProperties(data);
    const response = await apiInstance.post("/api/v1/verify/verify-code", cleanData);
    return response.data;
  } catch (error) {
    console.error("❌ Error in apiVerifyStampCode:", error);
    throw error;
  }
};

// API Check Internal QR Code - For business view
export const apiCheckInternalQrcode = async (serial: string): Promise<APIResponse<any>> => {
  try {
    const response = await apiInstance.post(
      "/api/v1/check-internal-qrcode",
      { serial },
      {
        headers: {
          x_scan: "qrx.com.vn",
          x_scan_internal: "qrx.com.vn",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("❌ Error in apiCheckInternalQrcode:", error);
    throw error;
  }
};

export { apiInstance };
